import { Component, OnInit, Input } from "@angular/core";
import { HelpdeskService } from "src/app/services/helpdesk.service";
import { Observable, of, Subject, Subscription } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  catchError,
  tap
} from "rxjs/operators";
import { Dealer } from "src/app/interfaces/dealer";
import { ApiResponse } from "src/app/interfaces/api-response";
import { User } from "src/app/interfaces/user";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ConfirmDialogComponent } from "../../modal-components/confirm-dialog/confirm-dialog.component";
import { UserOptionsComponent } from "../user-options/user-options.component";
import { AccountService } from "src/app/services/account.service";
import { ApplicationService } from "src/app/services/application.service";
import { ActivatedRoute, Router } from "@angular/router";
import { BasketService } from "src/app/services/basket.service";
import { CompanyGroup } from "src/app/interfaces/company-group";
import * as appGlobal from "src/app/globals";
import { FormGroup, FormBuilder, FormControl, Validators } from "@angular/forms";
import { AlertService } from "src/app/services/alert.service";
import { TranslateService } from "@ngx-translate/core";
import { HelperService } from "src/app/services/helper.service";

@Component({
    selector: "app-search-dealers",
    templateUrl: "./search-dealers.component.html",
    styleUrls: ["./search-dealers.component.scss"],
    standalone: false
})
export class SearchDealersComponent implements OnInit {
  @Input() redirectUrl = "";
  selectedIndex: number;
  searching: boolean;
  searchFailed: boolean;
  foccused = new Subject<string>();
  dealers: Dealer[];
  selectedDealer: Dealer;
  lastSearch: string;
  users: User[];
  last_activity: string;
  keyWord: string;
  loading: boolean;
  formGroup: FormGroup;
  usersCall: Subscription = null;
  frequentDealers: Array<{dealer_code: string, name: string, count: number}> = [];
  constructor(
    private helpdeskService: HelpdeskService,
    private modalService: NgbModal,
    private accountService: AccountService,
    private applicationService: ApplicationService,
    private basketService: BasketService,
    private router: Router,
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    private translateService: TranslateService,
    private helperService: HelperService,
    private route: ActivatedRoute,
  ) { }

  ngOnInit() {

     this.formGroup = this.formBuilder.group({
      searchText: new FormControl( this.helpdeskService.getLastSearch(this.route.snapshot.paramMap.get('dealerid')), [Validators.required, Validators.minLength(2)])
     });

    if (this.helpdeskService.getLastSearch(this.route.snapshot.paramMap.get('dealerid'))) {
      this.helpdeskService.searchDealers(this.helpdeskService.getLastSearch(this.route.snapshot.paramMap.get('dealerid'))).subscribe(data => {
        this.dealers = data;
      });
    }

    // Load frequently searched dealers
    this.loadFrequentDealers();
  }

  // Select dealer and show the corresponding users
  selectDealer(dealer: Dealer) {
    this.selectedDealer = dealer;

    // abort previous call
    if (this.usersCall !== null) {
      this.usersCall.unsubscribe();
    }

    this.applicationService.showLoader(true);
    this.usersCall = this.helpdeskService
      .getUsers(dealer.dealer_code)
      .subscribe((apiResponse: ApiResponse) => {
        this.applicationService.hideLoader();
        if (this.helperService.checkResponse(apiResponse)) {
          this.users = apiResponse.result["users"];
          this.last_activity = apiResponse.result["last_activity"];
        }
      });
  }

  showOptions(user: User) {
    const modal = this.modalService.open(ConfirmDialogComponent, {
      container: "#modalContainer"
    });

    modal.componentInstance.title = user.username;
    user.dealerIsOktaAccount = this.selectedDealer.okta;
    modal.componentInstance.setContent(UserOptionsComponent, "user", user);
    modal.dismissed.subscribe(() => {
      this.selectDealer(this.selectedDealer);
    })
  }

  // Select the user
  selectUser(user: User) {
    // Track dealer selection
    this.trackDealerSearch(this.selectedDealer);
    
    this.applicationService.showLoader(true);
    const bearerToken = localStorage.getItem("bearerToken");
    const refreshToken = localStorage.getItem("refreshToken");
    const preferedDefaultCompanyGroupCode = localStorage.getItem("defaultCompanyGroup") !== null ? localStorage.getItem("defaultCompanyGroup") : this.applicationService.getSelectCompanyGroupCode();
    this.helpdeskService.logInAsUser(user).then(() => {
      this.accountService
        .getAccountSettings()
        .subscribe((apiResponse: ApiResponse) => {
          this.applicationService.hideLoader();
          if (this.helperService.checkResponse(apiResponse)) {
            this.applicationService.saveAccountSettings(apiResponse.result);
            this.accountService.saveDealerInformation(this.selectedDealer);

            // Save the account code
            this.applicationService.saveSetting(
              "accountCode",
              this.selectedDealer.dealer_code
            );


            localStorage.removeItem("companyGroups");
            sessionStorage.removeItem("cgc");
            sessionStorage.clear();
            appGlobal.clearSettings();
            // Get available company groups
            this.accountService
              .getAvailableCompanyGroups()
              .subscribe((response: ApiResponse) => {
                if (response.success) {
                  let companyGroup = "ANL";
                  this.applicationService.saveCompanyGroups(response.result);
                  const companyGroups: CompanyGroup[] = response.result;

                  // Check if dealer has access to prefered default company 
                  const preferedDefaultCompanyGroup = (companyGroups.find((c: CompanyGroup) => {
                    return c.company_group_code === preferedDefaultCompanyGroupCode
                  }))

                  if (preferedDefaultCompanyGroup) {
                    companyGroup = preferedDefaultCompanyGroup.company_group_code;
                    this.applicationService.saveSelectedCompanyGroupCode(companyGroup);
                  } else {
                    if (companyGroups && companyGroups.length) {
                      companyGroup = companyGroups[0].company_group_code;
                    }
                  }

                  if (!this.redirectUrl || preferedDefaultCompanyGroup) {
                    this.redirectUrl = `/${companyGroup}/home`;
                  }

                  window.location.href = this.redirectUrl;
                  localStorage.setItem("refresh", "1");
                } else {
                  this.alertService.showErrorNotification(
                    `${this.translateService.instant(
                      "ERROR"
                    )}: Dealer niet geactiveerd!`
                  );
                  localStorage.setItem("bearerToken", bearerToken);
                  localStorage.setItem("refreshToken", refreshToken);
                }
              });
          }
        });
    });
  }

  onPaste(event: ClipboardEvent) {
    const el = document.getElementById("framenumberInput") as HTMLInputElement;
    const clipboardData = event.clipboardData || window["clipboardData"];
    this.formGroup.get("searchText").setValue(clipboardData.getData("text"));
    this.searchDealers(event);
  }

  searchDealers(e) {
    this.search();
    e.preventDefault();
    return false;
  }

  // Track dealer search in localStorage
  private trackDealerSearch(dealer: Dealer) {
    if (!dealer) return;
    
    const storageKey = 'helpdesk_dealer_searches';
    let searches = this.getDealerSearches();
    
    // Find existing entry or create new one
    const existingIndex = searches.findIndex(s => s.dealer_code === dealer.dealer_code);
    
    if (existingIndex >= 0) {
      searches[existingIndex].count++;
      searches[existingIndex].name = dealer.name; // Update name in case it changed
      searches[existingIndex].lastSearched = new Date().toISOString();
    } else {
      searches.push({
        dealer_code: dealer.dealer_code,
        name: dealer.name,
        count: 1,
        lastSearched: new Date().toISOString()
      });
    }
    
    // Save back to localStorage
    localStorage.setItem(storageKey, JSON.stringify(searches));
    
    // Update frequent dealers list
    this.loadFrequentDealers();
  }
  
  // Get dealer searches from localStorage
  private getDealerSearches(): Array<{dealer_code: string, name: string, count: number, lastSearched: string}> {
    const storageKey = 'helpdesk_dealer_searches';
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : [];
  }
  
  // Load dealers that have been searched more than 3 times
  private loadFrequentDealers() {
    const searches = this.getDealerSearches();
    
    // Filter dealers with more than 3 searches and sort by count descending
    this.frequentDealers = searches
      .filter(s => s.count > 3)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Limit to top 10
  }
  
  // Load dealer by dealer code (from chip click)
  loadDealerByCode(dealerCode: string) {
    this.applicationService.showLoader(true);
    
    // Search for the dealer by code
    this.helpdeskService.searchDealers(dealerCode).subscribe((dealers: Dealer[]) => {
      this.applicationService.hideLoader();
      
      if (dealers && dealers.length > 0) {
        // Find exact match by dealer_code
        const dealer = dealers.find(d => d.dealer_code === dealerCode) || dealers[0];
        this.dealers = dealers;
        this.selectDealer(dealer);
      } else {
        this.alertService.showErrorNotification(
          this.translateService.instant('DEALER_NOT_FOUND')
        );
      }
    });
  }

  search() {
    this.users = [];
    const keyword = String(this.formGroup.get("searchText").value).replace(/(\r\n|\n|\r)/gm, "").trim();

    this.formGroup.get("searchText").setValue(keyword);
    if (this.formGroup.valid) {
      this.searching = true;
      setTimeout(()=> {
        this.searching = false;
      }, 1000);
      this.helpdeskService.searchDealers(keyword).subscribe((dealers: Dealer[]) => {
        this.searching = false;
        if (dealers && dealers.length) {
          this.helpdeskService.saveLastSearch(keyword);
          this.searchFailed = false;
          this.dealers = dealers;
        } else {
          this.dealers = [];
        }
      })
    }
  }
}
